import { v4 as uuidv4 } from "uuid";
import { ColumnJson, DataTableDataInterface } from "app/utils/types";

export const editableColumns: string[] = [
  "employeePremium",
  "employerPremium",
  "employerAdminFee",
  "planOriginationFees",
  "planLoanRate",
  "minimumAFR",
  "employeePrincipalPayment",
];

export const planAdminSplitDollarTableDataJson: DataTableDataInterface = {
  pagination: 10,
  rowsPerPage: [25, 50, 100],
  styles: {
    minWidth: "95rem",
    minHeight: "",
  },
  scrollable: true,
  scrollHeight: "500px",
  onRowClickNavigateTo: "/agent/contact",
  column: [],
};
export const planAdminSplitDollarColumnSchema = (
  label: string,
  field: string,
  isFrozen = false,
  classNames?: string,
): ColumnJson => {
  let newLabel = label;
  if (label === "Employee Out Of Pocket") {
    newLabel = "Employee Out of Pocket";
  }

  // Special handling for financingExit radio button
  if (field === "financingExit") {
    return {
      id: `${field}-${uuidv4()}`,
      body: {},
      field,
      label: newLabel,
      frozen: isFrozen,
      header: false,
      filters: {
        filterType: "text",
        value: "",
      },
      editColumn: false, // Disable editing to prevent text input
      align: "center", // Center align for radio button
      alignHeader: "center",
      editorType: "text", // Use text type but we'll override with custom renderer
      classNames,
    };
  }

  return {
    id: `${field}-${uuidv4()}`,
    body: {},
    field,
    label: newLabel,
    frozen: isFrozen,
    header: false,
    filters: {
      filterType: "text",
      value: "",
    },
    editColumn: editableColumns.findIndex((col) => col === field) > -1,
    align: "right",
    alignHeader: "center",
    editorType: "decimal",
    classNames,
    ...((field === "planLoanRate" || field === "minimumAFR") && {
      maxFractionDigits: 3,
    }),
  };
};

export const headerColumnGroup = {
  EOP: ["employeePremium", "employeeInterestPayment", "employeeOOP"],
  EOPP: ["employerPremium", "employerAdminFee", "planOriginationFees", "employerOOP"],
  TPPIC: ["totalPolicyPremium"],
  "INR%": ["planLoanRate", "minimumAFR"],
  INR$: ["planInterest", "minimumAFRInterest"],
  IC: ["imputedCompensationToEmployee"],
  OB: ["boyOutstandingLoanBalance", "eoyOutstandingLoanBalance"],
  EPP: ["employeePrincipalPayment"],
};

export const commonColumns = ["policyYear", "insuredAge", "planYear", "financingExit"];

const parseAccrueInterest = (accrueInterest: string | number | boolean) => {
  // `accrueInterest` is one of: "CAPITALIZED", "OUT_OF_POCKET_FULL", or a
  // partial out-of-pocket amount typecast to a string/number (e.g. "500").
  // Legacy boolean values are also handled for backwards compatibility.
  const accrueInterestStr =
    accrueInterest === null || accrueInterest === undefined ? "" : String(accrueInterest);
  const isCapitalized = accrueInterestStr === "CAPITALIZED" || accrueInterest === true;
  const isOutOfPocketFull = accrueInterestStr === "OUT_OF_POCKET_FULL" || accrueInterest === false;
  const partialAmount =
    !isCapitalized &&
    !isOutOfPocketFull &&
    accrueInterestStr !== "" &&
    !Number.isNaN(Number(accrueInterestStr))
      ? Number(accrueInterestStr)
      : null;
  return { isCapitalized, partialAmount };
};

// A balance that should be exactly zero (e.g. a fully paid-off loan) can come out as a
// tiny non-zero residual — 0.007, 0.5 — from floating-point arithmetic or rounding in
// upstream inputs. Anything under $1 is noise, not a real balance, so it's snapped to a
// clean 0; anything $1 or more is a real amount and is left exactly as computed.
const ZERO_SNAP_THRESHOLD = 1;
const snapNearZero = (value: number) => (Math.abs(value) < ZERO_SNAP_THRESHOLD ? 0 : value);

const computeDerivedRow = (
  row: any,
  prevRow: any,
  accrueMode: ReturnType<typeof parseAccrueInterest>,
) => {
  const { isCapitalized, partialAmount } = accrueMode;
  const updated = { ...row };

  // Employee Principal Payment (S):
  //   Exit row: always equals the prior year's EOY Outstanding Loan Balance.
  //   Any other row: whatever is already on the row (0, or a manual partial payment).
  updated.employeePrincipalPayment = updated.financingExit
    ? +(prevRow ? prevRow.eoyOutstandingLoanBalance : 0)
    : +updated.employeePrincipalPayment || 0;

  // BOY Outstanding Loan Balance (Q)
  //   First Year  (Q4 = G4):        BOY = Employer Premium
  //   Subsequent  (Qn = Gn + Rn-1 - Sn):
  //     BOY = Prior Year's EOY + Current Employer Premium
  //           - Current Year's Employee Principal Payment
  // The first row is a special case and does NOT subtract the Employee
  // Principal Payment. Subsequent rows subtract the CURRENT row's value.
  updated.boyOutstandingLoanBalance = snapNearZero(
    prevRow
      ? +prevRow.eoyOutstandingLoanBalance +
          +updated.employerPremium -
          updated.employeePrincipalPayment
      : +updated.employerPremium,
  );

  updated.planInterest = (+updated.planLoanRate / 100) * +updated.boyOutstandingLoanBalance;

  if (isCapitalized) {
    updated.employeeInterestPayment = 0;
  } else if (partialAmount !== null) {
    updated.employeeInterestPayment = partialAmount;
  } else {
    updated.employeeInterestPayment = +updated.planInterest;
  }

  updated.employeeOOP = +updated.employeePremium + +updated.employeeInterestPayment;

  updated.employerOOP =
    +updated.employerPremium + +updated.employerAdminFee + +updated.planOriginationFees;

  updated.totalPolicyPremium = +updated.employerPremium + +updated.employeePremium;

  updated.minimumAFRInterest = (+updated.minimumAFR / 100) * +updated.boyOutstandingLoanBalance;

  if (isCapitalized) {
    updated.imputedCompensationToEmployee = 0;
  } else {
    updated.imputedCompensationToEmployee = Math.max(
      +updated.minimumAFRInterest - +updated.employeeInterestPayment,
      0,
    );
  }

  // EOY Outstanding Loan Balance =
  //   BOY + MAX(Plan Interest, Minimum AFR Interest) - Employee Interest Payment
  // Applied regardless of the accrueInterest flag: employeeInterestPayment is
  // already forced to 0 when accrueInterest === true, so the same formula holds.
  updated.eoyOutstandingLoanBalance = snapNearZero(
    Math.max(
      +updated.boyOutstandingLoanBalance +
        Math.max(+updated.planInterest, +updated.minimumAFRInterest) -
        +updated.employeeInterestPayment,
      0,
    ),
  );

  return updated;
};

const buildSplitDollarRowArr = (rowData: any[], fromIdx: number, planSDSchema: any) => {
  const keys1 = Object.keys(planSDSchema);
  return rowData.slice(fromIdx).map((row) => {
    const keys2 = Object.keys(row);
    const commonKeys = keys1.filter((k) => keys2.includes(k));
    const splitDollarRow = commonKeys.reduce((acc, k) => ({ ...acc, [k]: row[k] }), {});
    return {
      ...splitDollarRow,
      endOfYearOutstandingLoanBalance: row.eoyOutstandingLoanBalance,
    };
  });
};

export const prepareRows = (
  rowData: any,
  key: string,
  value: number,
  rowIdx: number,
  planSDSchema: any,
  policyInputCSVData: any,
  accrueInterest: string | number | boolean,
) => {
  rowData[rowIdx] = { ...rowData[rowIdx], [key]: value };

  const accrueMode = parseAccrueInterest(accrueInterest);

  for (let i = rowIdx; i < rowData.length; i++) {
    const prevRow = i > 0 ? rowData[i - 1] : null;
    rowData[i] = computeDerivedRow(rowData[i], prevRow, accrueMode);
  }

  const splitDollarRowArr = buildSplitDollarRowArr(rowData, rowIdx, planSDSchema);

  return {
    adminSplitDollarRow: rowData[rowIdx],
    splitDollarRowArr,
    updatedRows: rowData,
  };
};

// Moves the financing-exit selection to `newExitRowIndex` and recomputes every row's
// BOY/EOY balance and payment chain from scratch. The row that used to hold the flag
// has its Employee Principal Payment reset to 0 — that forced value only made sense
// while it was the exit row, and would otherwise linger as stale data and re-zero the
// balance at that row (and cascade zeros through every later row) once the exit moves.
export const applyFinancingExitChange = (
  rowData: any[],
  newExitRowIndex: number,
  planSDSchema: any,
  accrueInterest: string | number | boolean,
) => {
  const updatedRows = rowData.map((row, idx) => {
    const wasExit = row.financingExit === true;
    const isExit = idx === newExitRowIndex;
    return {
      ...row,
      financingExit: isExit,
      employeePrincipalPayment: wasExit && !isExit ? 0 : row.employeePrincipalPayment,
    };
  });

  const accrueMode = parseAccrueInterest(accrueInterest);

  for (let i = 0; i < updatedRows.length; i++) {
    const prevRow = i > 0 ? updatedRows[i - 1] : null;
    updatedRows[i] = computeDerivedRow(updatedRows[i], prevRow, accrueMode);
  }

  const splitDollarRowArr = buildSplitDollarRowArr(updatedRows, 0, planSDSchema);

  return { updatedRows, splitDollarRowArr };
};
